import {
  CreateChartOptionsWithConfig,
  HorizonWithPriceRange,
  RangeHorizon,
  TimeSeriesItem,
} from '@toggle/chart';
import { Toggle } from '@toggle/design-system';
import { Entity, ExploreSingleInsightWithMeta } from '@toggle/toggle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPriceHorizonSettings } from '~/modules/simple-chart/utils/simple-chart-utils';
import { useExploreStore } from '~/views/explore/hooks/use-explore-store/useExploreStore';
import { ChartState } from '~/views/turbo-chart/hooks/use-chart-state/useChartState';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import { PriceChartArea } from '../price-chart-area/PriceChartArea';
import * as S from './PriceChart.styles';
import { TerminalChartAssetData, TerminalPaneData } from '~/views/turbo-chart/utils/chart-utils/types';

export const HORIZONS = [
  RangeHorizon.OneWeek,
  RangeHorizon.TwoWeeks,
  RangeHorizon.OneMonth,
  RangeHorizon.TwoMonths,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
];

export const EVENT_CONFIG = {
  TMI: true,
};

const CHART_CONFIG: Partial<CreateChartOptionsWithConfig['config']> = {
  enableZoom: false,
  enableHover: true,
  hoverVariant: 'simple',
  lineGradient: [
    {
      stop: 0,
      opacity: 0.5,
    },
    {
      stop: 1,
      opacity: 0.1,
    },
  ],
};

export interface PriceChartProps {
  chartState: ChartState;
  chartData: TerminalChartAssetData[];
  dailyTimeseries?: TimeSeriesItem[];
  horizonRanges: RangeHorizon[];
  hasError: boolean;
  hasEmpty: boolean;
  assetWithError?: Entity;
  primaryAsset?: TerminalChartAssetData;
  primaryPane?: TerminalPaneData;
  activeInsightId: string;
  selectedInsights: ExploreSingleInsightWithMeta[];
  filteredOnly: boolean;
  retryInitialChartLoad: () => void;
  changeRange: (range: RangeHorizon) => Promise<void>;
  handleChangeChartHorizon: (horizon: RangeHorizon) => void;
  setFilteredOnly: (isFilteredOnly: boolean) => void;
  handleSetActiveInsightId: (id: string) => void;
  entity: Entity;
}

export const PriceChart = ({
  chartState,
  chartData,
  dailyTimeseries,
  horizonRanges,
  hasError,
  hasEmpty,
  assetWithError,
  primaryAsset,
  primaryPane,
  activeInsightId = '',
  selectedInsights,
  filteredOnly,
  retryInitialChartLoad,
  changeRange,
  handleChangeChartHorizon,
  setFilteredOnly,
  handleSetActiveInsightId,
  entity,
}: PriceChartProps) => {
  const { t } = useTranslation(['widget', 'insights']);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [horizonWithPriceRanges, setHorizonWithPriceRanges] = useState<
    HorizonWithPriceRange[]
  >([]);

  const { colors: themeColors } = useChartTheme();
  const activeHorizonIdx = useMemo(() => {
    return HORIZONS.findIndex(horizon => horizon === chartState.horizon);
  }, [chartState.horizon]);
  const { getShouldShowToggleFiltered } = useExploreStore(state => ({
    getShouldShowToggleFiltered: state.getShouldShowToggleFiltered,
  }));

  useEffect(() => {
    if (dailyTimeseries && chartData[0]) {
      const { rangesWithPrice, lineColorToken } = getPriceHorizonSettings({
        horizonRanges,
        dailyTimeseries,
        horizon: HORIZONS[activeHorizonIdx],
        colors: themeColors,
      });

      if (lineColorToken) {
        chartData[0].lineColorToken = lineColorToken;
      }

      setHorizonWithPriceRanges(rangesWithPrice);
    }
  }, [dailyTimeseries]);

  const horizonsLabels = HORIZONS.map(horizon =>
    /* i18next-extract-mark-context-next-line ["OneWeek", "TwoWeeks", "OneMonth", "TwoMonths", "ThreeMonths", "SixMonths"] */
    t('widget:insights.insightHorizon', { context: horizon })
  );

  const handleChangeHorizon = (index: number) => {
    if (!chartData?.length) {
      const found = horizonWithPriceRanges.find(
        h => h.horizon === HORIZONS[index]
      );

      if (found) {
        chartData[0].lineColorToken = found.lineColorToken;
      }
    }

    changeRange(HORIZONS[index]);
    handleChangeChartHorizon(HORIZONS[index]);
  };

  const isLoading = !hasError && !hasEmpty && !dailyTimeseries && !chartData[0];

  if (isLoading) {
    return <S.StyledChartLoading isLegacyVariant isChatMessage={false} />;
  }

  const showToggle = getShouldShowToggleFiltered();

  if (!primaryAsset || !primaryPane) {
    return null;
  }

  return (
    <>
      <S.Wrapper>
        <S.StyledButtonGroup
          labels={horizonsLabels}
          onClick={handleChangeHorizon}
          activeIndex={activeHorizonIdx}
          $activeIndex={activeHorizonIdx}
        />
        {showToggle && (
          <Toggle
            rightLabel={t('insights:toggleFiltered')}
            isChecked={filteredOnly}
            onToggle={setFilteredOnly}
          />
        )}
      </S.Wrapper>
      <S.StyledChartAreaWrapper
        assetWithError={assetWithError}
        hasError={hasError}
        hasEmpty={hasEmpty}
        colors={themeColors}
        chartData={chartData}
        chartConfig={CHART_CONFIG}
        onRetry={retryInitialChartLoad}
        renderChartArea={options => (
          <PriceChartArea
            canvasRef={canvasRef}
            options={options}
            chartState={chartState}
            primaryAsset={primaryAsset}
            primaryPane={primaryPane}
            selectedInsights={selectedInsights}
            activeInsightId={activeInsightId}
            dailyTimeseries={dailyTimeseries}
            enablePriceHover
            handleSetActiveInsightId={handleSetActiveInsightId}
            entity={entity}
          />
        )}
      />
    </>
  );
};
